import React from 'react';
import { FormattedMessage } from '@kyruus/intl';
import { useLocation } from 'react-router-dom';
import { Box } from '@biome-design-system/ui/Box';
import { Typography } from '@biome-design-system/ui/Typography';
import type { PMCConfig } from '@kyruus/types';
import {
  type GetUpdatedSearch,
  type Log,
  type SearchSummary as SearchSummaryType
} from 'Src/types';
import { SearchSummaryMessage } from './SearchSummaryMessage';
import { ClearFilters } from './ClearFilters';
import { messages } from '../messages';

interface SearchSummaryProps {
  config: PMCConfig;
  totalProviders: number;
  searchSummary: SearchSummaryType;
  getUpdatedSearch: GetUpdatedSearch;
  locationName?: string | null; // location name for map-type searches
  hideSearchDescription?: boolean; // description is hidden when rendering in location page provider drawer
  log: Log;
  showClearLink?: boolean; // option to hide clear link when needed
  isLoadingOverlayShown?: boolean; // option to hide text when loading overlay is shown
}

export const SearchSummary = ({
  config,
  totalProviders,
  searchSummary,
  getUpdatedSearch,
  locationName,
  hideSearchDescription, // description is hidden when rendering in location page provider drawer
  log,
  showClearLink = true,
  isLoadingOverlayShown = false
}: SearchSummaryProps) => {
  const location = useLocation();
  const shouldShowClearLink =
    showClearLink &&
    (searchSummary.has_filters ||
      searchSummary.filter?.length ||
      searchSummary.location);
  const isMapSearch = locationName != null;
  return (
    <Box>
      <Typography
        className="hidden-print"
        data-testid="search-summary-text"
        sx={{ display: 'inline-block', mr: 2 }}
      >
        <Typography component="span" aria-live="polite">
          {!isLoadingOverlayShown && (
            <SearchSummaryMessage
              searchSummary={searchSummary}
              hideSearchDescription={hideSearchDescription}
              totalProviders={totalProviders}
              isMapSearch={isMapSearch}
              getUpdatedSearch={getUpdatedSearch}
              log={log}
              queryString={location.search}
              config={config}
            />
          )}
        </Typography>
        {locationName && (
          <Typography component="span" aria-live="polite" sx={{ ml: 0.5 }}>
            {!isLoadingOverlayShown && (
              <FormattedMessage
                {...messages.locationName}
                values={{
                  location: locationName
                }}
              />
            )}
          </Typography>
        )}
      </Typography>
      {shouldShowClearLink && (
        <ClearFilters getUpdatedSearch={getUpdatedSearch} log={log} />
      )}
    </Box>
  );
};
